import type { Env, FilterValue, ResourceType } from "./types";

/** Filter chips, in render order. */
export const FILTER_BUTTONS: ReadonlyArray<{
  label: string;
  value: FilterValue;
}> = [
  { label: "ALL RESOURCES", value: "all" },
  { label: "EBOOKS", value: "ebook" },
  { label: "REPORTS", value: "report" },
  { label: "WEBINARS", value: "webinar" },
  { label: "WHITEPAPERS", value: "whitepaper" },
];

/**
 * Eyebrow label rendered on each card.
 *
 * Singular and in normal case, because it labels one resource: a card in the
 * E-Books collection reads "E-Book". The plural, upper-case forms belong to the
 * filter controls, which name a group rather than an item — those live on the
 * Webflow page now, with `FILTER_BUTTONS` above serving only this app's own
 * `/resources-list`.
 *
 * `.resource-card-tag` sets no `text-transform`, so these strings render
 * exactly as written — casing here is what appears on the card.
 *
 * Also indexed for search (`SEARCHABLE_FIELDS` in `resources.ts`), so a visitor
 * typing "whitepaper" matches every whitepaper.
 */
export const TYPE_LABEL: Record<ResourceType, string> = {
  ebook: "E-Book",
  report: "Analyst report",
  webinar: "Webinar",
  whitepaper: "Whitepaper",
};

/**
 * PUBLIC filter slugs — the vocabulary everything outside this app speaks.
 *
 * These are the `?type=` values, the arguments `window.ResourcesEmbed.setFilter()`
 * expects, and what `resources-embed:change` reports — so they are also the
 * values to put in `data-resource-filter` on the Webflow page.
 *
 * Plural, because a filter selects a group. They are deliberately NOT the
 * internal `ResourceType` union, which stays singular (`ebook`, `report`, …)
 * because it names one item and is what `TYPE_LABEL`, `TYPE_PRIORITY`,
 * `COLLECTION_SLUG` and `ENV_VAR_BY_TYPE` are all keyed on. Translating at the
 * boundary keeps a rename of the public vocabulary from rippling through the
 * mapper, the sort and the env plumbing.
 *
 * Not to be confused with `COLLECTION_SLUG` below: that is the Webflow CMS
 * page path (`/resources/reports/<item>`) and is set by the site's collection
 * settings, not by us. The two differ for reports and must be free to.
 */
export const FILTER_SLUG: Record<ResourceType, string> = {
  ebook: "ebooks",
  report: "analyst-reports",
  webinar: "webinars",
  whitepaper: "whitepapers",
};

/** Public slug → internal type. Built from `FILTER_SLUG` so they cannot drift. */
export const TYPE_BY_FILTER_SLUG: Record<string, ResourceType> = Object.fromEntries(
  Object.entries(FILTER_SLUG).map(([type, slug]) => [slug, type as ResourceType]),
) as Record<string, ResourceType>;

/** Tiebreaker when two cards share a publish timestamp. Lower wins. */
export const TYPE_PRIORITY: Record<ResourceType, number> = {
  report: 1,
  whitepaper: 2,
  ebook: 3,
  webinar: 4,
};

/**
 * URL segment for each collection's detail pages.
 *
 * These paths are served by the Webflow site itself, not by this Astro app, so
 * they are deliberately root-absolute and NOT prefixed with Astro's `base`.
 * Set each value to the Webflow collection's "Collection page" slug — visible
 * in Webflow under CMS → <collection> → Settings → Collection URL.
 */
export const COLLECTION_SLUG: Record<ResourceType, string> = {
  ebook: "booklet",
  report: "analyst-report",
  webinar: "video-on-demand",
  whitepaper: "whitepaper",
};

export function buildResourceUrl(type: ResourceType, itemSlug: string): string {
  return `/resources/${COLLECTION_SLUG[type]}/${itemSlug}`;
}

/** Which env var carries each collection's ID. */
export const ENV_VAR_BY_TYPE = {
  ebook: "CMS_EBOOKS",
  report: "CMS_REPORTS",
  webinar: "CMS_WEBINARS",
  whitepaper: "CMS_WHITEPAPERS",
} as const satisfies Record<ResourceType, string>;

export function getCollectionId(type: ResourceType, env: Env): string {
  const envVar = ENV_VAR_BY_TYPE[type];
  const value = env[envVar];
  if (!value) {
    throw new Error(
      `Missing env var ${envVar}. Set it in .dev.vars locally, or in the Webflow Cloud dashboard for staging/production.`,
    );
  }
  return value;
}

/** Resource types matched by a filter chip. */
export function typesForFilter(filter: FilterValue): ResourceType[] | "all" {
  return filter === "all" ? "all" : [filter];
}
